import React, { useEffect, useState } from 'react';

import { FormattedMessage } from '../../util/reactIntl';
import { fetchProjectsForListing } from '../../util/api';
import { DESIGN_SHOWCASE_ENABLED } from '../../config/configFeatures';

import { Heading, NamedLink } from '../../components';

import css from './SectionFeaturedProjectsMaybe.module.css';

/**
 * Matterax showcase→catalogue connection: "Seen in real projects".
 *
 * On a MATERIAL listing page, surfaces the showcase projects that tagged this
 * product (design-showcase listings whose designProducts include this listing
 * id) — the studio is credited on every card. This is the product half of the
 * landing promise: "when someone looks a material up, your studio is right
 * there next to it."
 *
 * Data comes from GET /api/projects-for-listing (server-side 5-min cache), so
 * the section costs one tiny fetch and renders nothing until (and unless)
 * there are projects to show. Never renders on design listings themselves —
 * they have the reverse section (materials in this project) instead.
 */
const SectionFeaturedProjectsMaybe = props => {
  const { listingId, isDesign } = props;
  const [projects, setProjects] = useState(null);

  useEffect(() => {
    // Gate the FETCH, not just the paint: otherwise every material listing
    // page still calls /api/projects-for-listing for a feature that is off.
    if (!DESIGN_SHOWCASE_ENABLED || !listingId || isDesign) return undefined;
    let alive = true;
    fetchProjectsForListing(listingId)
      .then(r => {
        if (alive) setProjects(Array.isArray(r?.projects) ? r.projects : []);
      })
      .catch(() => {
        if (alive) setProjects([]);
      });
    return () => {
      alive = false;
    };
  }, [listingId, isDesign]);

  if (!DESIGN_SHOWCASE_ENABLED || isDesign || !projects || projects.length === 0) {
    return null;
  }

  return (
    <section className={css.root}>
      <Heading as="h2" rootClassName={css.title}>
        <FormattedMessage
          id="ListingPage.featuredProjectsTitle"
          values={{ count: projects.length }}
        />
      </Heading>
      <p className={css.subtitle}>
        <FormattedMessage id="ListingPage.featuredProjectsSubtitle" />
      </p>
      <div className={css.grid}>
        {projects.map(p => (
          <NamedLink
            key={p.id}
            name="ListingPage"
            params={{ id: p.id, slug: p.slug }}
            className={css.card}
          >
            {p.imageUrl ? (
              <img
                className={css.cardImage}
                src={p.imageUrl}
                srcSet={p.imageUrl2x ? `${p.imageUrl} 1x, ${p.imageUrl2x} 2x` : undefined}
                alt={p.title}
                loading="lazy"
              />
            ) : (
              <div className={css.cardImageFallback} />
            )}
            <div className={css.cardText}>
              <span className={css.cardTitle}>{p.title}</span>
              {p.studio ? (
                <span className={css.cardStudio}>
                  <FormattedMessage
                    id="ListingPage.featuredProjectsBy"
                    values={{ studio: p.studio }}
                  />
                </span>
              ) : null}
            </div>
          </NamedLink>
        ))}
      </div>
    </section>
  );
};

export default SectionFeaturedProjectsMaybe;
